
import Vue from 'vue';
import Component from 'vue-class-component';
import { Prop } from 'vue-property-decorator';

export interface IStackedColumnCategory {
  key: string;
  // short label under the column
  label: string;
  // label in the tooltip and the table view
  longLabel: string;
}

export interface IStackedColumnSeries {
  key: string | number;
  label: string;
  // e.g. what a folded "Other" series contains, shown on hover of its legend entry
  sublabel?: string;
  color: string;
  // one value per category, aligned with the categories prop
  values: number[];
}

/**
 * Columns stacked by series, first series at the baseline: thin rounded columns with a surface gap between
 * segments, hairline grid with round ticks, the peak total labelled, a legend with every series total,
 * a tooltip per column (hover and keyboard focus) and a table view twin.
 */
@Component({
  name: 'StackedColumnChart',
})
export default class StackedColumnChart extends Vue {
  @Prop({ required: true })
  protected categories!: IStackedColumnCategory[];

  @Prop({ required: true })
  protected series!: IStackedColumnSeries[];

  // Plural noun of what is counted ("sign-ups"), used in the column aria-label.
  @Prop({ required: true })
  protected unitLabel!: string;

  @Prop({ default: '' })
  protected totalCaption!: string;

  @Prop({ default: '' })
  protected emptyLabel!: string;

  // Header of the first table column ("Month").
  @Prop({ default: '' })
  protected categoryHeader!: string;

  @Prop({ default: 'Total' })
  protected totalHeader!: string;

  protected hovered: number | null = null;
  protected showTable: boolean = false;

  get total(): number {
    return this.series.reduce((sum, each) => sum + this.seriesTotal(each), 0);
  }

  get reversedSeries(): IStackedColumnSeries[] {
    return this.series.slice().reverse();
  }

  // newest category first in the table view
  get reversedIndexes(): number[] {
    return this.categories.map((_category, index) => index).reverse();
  }

  get peakIndex(): number {
    let peak = -1;
    this.categories.forEach((_category, index) => {
      const value = this.columnTotal(index);
      if (value > 0 && (peak === -1 || value > this.columnTotal(peak))) {
        peak = index;
      }
    });

    return peak;
  }

  // Round step (1, 2, 5 × 10^n) giving about three gridlines above the baseline.
  get step(): number {
    const raw = this.maxColumn / 3;
    const magnitude = Math.pow(10, Math.floor(Math.log10(raw)));
    const nice = [1, 2, 5, 10].find(factor => factor * magnitude >= raw) || 10;

    return Math.max(1, nice * magnitude);
  }

  get maxColumn(): number {
    return Math.max(1, ...this.categories.map((_category, index) => this.columnTotal(index)));
  }

  get scaleMax(): number {
    return Math.ceil(this.maxColumn / this.step) * this.step;
  }

  get ticks(): number[] {
    const ticks: number[] = [];
    for (let tick = 0; tick <= this.scaleMax; tick += this.step) {
      ticks.push(tick);
    }

    return ticks;
  }

  seriesTotal(series: IStackedColumnSeries): number {
    return series.values.reduce((sum, value) => sum + (value || 0), 0);
  }

  columnTotal(index: number): number {
    return this.series.reduce((sum, each) => sum + (each.values[index] || 0), 0);
  }

  // Top of the stack first: the column is drawn top-down.
  visibleSegments(index: number): IStackedColumnSeries[] {
    return this.reversedSeries.filter(each => (each.values[index] || 0) > 0);
  }

  segmentPercent(value: number, index: number): number {
    const columnTotal = this.columnTotal(index);

    return columnTotal ? ((value || 0) / columnTotal) * 100 : 0;
  }

  barPercent(value: number): number {
    return (value / this.scaleMax) * 100;
  }

  tickPercent(tick: number): number {
    return (tick / this.scaleMax) * 100;
  }

  slotCenterPercent(index: number): number {
    return ((index + 0.5) / this.categories.length) * 100;
  }

  formatNumber(value: number): string {
    return value.toLocaleString(this.$i18n.locale);
  }
}
